import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   Icon-only control. Enforces two things a bare <button> won't:

   Label is required — used as both the accessible name and tooltip text.
   No way to ship an unlabelled icon button from here (the failure mode
   every icon-only control has).

   44px hit area via ::after, not padding. Visible control is 32px; a
   pseudo-element extends the touch target without changing layout.
   Padding the element itself would push its neighbours apart.

   Use for: drawer toggles, zoom controls, row overflow menus, close
   buttons. Not for primary actions.
*/

export const iconButtonVariants = cva(
  [
    "relative inline-grid place-items-center shrink-0",
    "rounded-md border",
    "transition-colors duration-120 ease-out",
    "[&_svg]:shrink-0 [&_svg]:pointer-events-none",
    // 44px hit area, centred, layout-neutral
    "after:absolute after:left-1/2 after:top-1/2",
    "after:size-11 after:-translate-x-1/2 after:-translate-y-1/2",
    "after:content-['']",
    "disabled:cursor-not-allowed disabled:bg-surface-sunken",
    "disabled:text-text-disabled disabled:border-border-soft",
    // the hit area must not swallow clicks meant for neighbours when disabled
    "disabled:after:hidden",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-surface border-border text-text-2",
          "hover:bg-surface-sunken hover:border-border-strong hover:text-text",
        ],
        ghost: [
          "bg-transparent border-transparent text-text-2",
          "hover:bg-surface-sunken hover:text-text",
        ],
        destructive: ["bg-surface border-err text-err", "hover:bg-err-tint"],
      },
      size: {
        sm: "size-7 [&_svg]:size-3.5",
        md: "size-8 [&_svg]:size-4",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
)

export interface IconButtonProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "aria-label">,
    VariantProps<typeof iconButtonVariants> {
  /** Verb phrase: "Open messages", "Zoom in", "Close". Accessible name and tooltip. */
  label: string
  /** A lucide icon. Sized by the variant; don't set size on it yourself. */
  icon: React.ReactNode
  /** Count pip on the top-right corner. Above 99 renders "99+". Announced in the accessible name. */
  count?: number
  /**
   * What the count counts. Default "unread".
   *
   * Also decides the badge's colour, because the two mean opposite things to a
   * reader: unread is work waiting, a total is a conversation already read.
   * Rendering both in the same alert red made a settled thread of three read
   * identically to three messages nobody had seen.
   */
  countLabel?: string
  tooltipSide?: "top" | "right" | "bottom" | "left"
  /** Suppress the tooltip when the control already sits inside one. */
  noTooltip?: boolean
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    className,
    variant,
    size,
    label,
    icon,
    count,
    countLabel = "unread",
    tooltipSide = "bottom",
    noTooltip = false,
    type = "button",
    ...props
  },
  ref,
) {
  const showCount = typeof count === "number" && count > 0
  const accessibleName = showCount ? `${label}, ${count} ${countLabel}` : label

  const control = (
    <button
      ref={ref}
      type={type}
      aria-label={accessibleName}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    >
      {icon}
    </button>
  )

  /*
     The badge is a sibling of the button inside a wrapper sized to hold both,
     never a child hung off the button's corner on negative offsets.

     Hung off the corner it lives outside the button's border box, so any
     ancestor that clips its overflow cuts it — which the finding card does, to
     keep its rounded edge — and every call site has to know to pad around it.
     Here the wrapper owns the extra 6px the badge needs, so nothing overhangs
     anything and no caller has to compensate.
  */
  const button = showCount ? (
    <span className="relative inline-flex pr-1.5 pt-1.5">
      {control}
      <span
        aria-hidden="true"
        className={cn(
          "absolute right-0 top-0",
          "grid h-4 min-w-4 place-items-center rounded-full px-1",
          /*
             Two tones, because the counts mean opposite things: unread is work
             waiting, a total is a conversation already read. Both in alert red
             made a settled thread of three read identically to three nobody
             had seen.

             A total takes info blue rather than graphite — `color-tokens.md`
             reserves that hue for "people and process", which is what a
             conversation is, and graphite made the badge disappear into the
             icon it sits on. It should still say "there is discussion here";
             only the alarm belongs to unread.
          */
          "border-2 border-surface text-white",
          countLabel === "unread" ? "bg-err" : "bg-info",
          /*
             The UI font, not `font-id`. `font-id` is the identifier face (IBM
             Plex Mono), there so reg numbers and rule ids align in columns; its
             `1` carries a typewriter foot that at this size reads as a stray
             bar under the digit. A count is not an identifier.

             9px rather than `text-micro`: that token is 11px with 0.12em
             tracking for eyebrow labels, and in a 12px inner circle a digit
             that size touches both edges and reads as a cropped badge.

             `leading-3` is the circle's 12px content box exactly (16px less the
             2px border each side), so the line box fills the track and the
             digit centres in the circle instead of sitting high in a line box
             smaller than its container.
          */
          "text-[9px] font-semibold leading-3 tracking-normal tabular-nums",
        )}
      >
        {count > 99 ? "99+" : count}
      </span>
    </span>
  ) : (
    control
  )

  if (noTooltip) return button

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{button}</TooltipPrimitive.Trigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </TooltipPrimitive.Root>
  )
})

/**
 * The app's tooltip surface: dark chip, caption text, a quick fade. Portalled, 6px off the
 * trigger by default. Put it inside a `TooltipPrimitive.Root`, next to the trigger, so every
 * tooltip looks the same and a restyle happens here once. `className` adds to it (a max
 * width, say).
 */
export const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(function TooltipContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-toast rounded-sm px-2 py-1",
          "bg-text text-caption text-[color:var(--surface)]",
          "shadow-e3",
          "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
          "data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0",
          "duration-120 motion-reduce:animate-none",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
})

/**
 * Mount once, near the root. Radix requires a provider for tooltips.
 *
 * The 500ms delay is deliberate: reviewers move fast through the top bar
 * and a 200ms tooltip fires on every pass. `skipDelayDuration` keeps
 * subsequent tooltips instant once the first has opened.
 */
export const TooltipProvider = ({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider delayDuration={500} skipDelayDuration={300} {...props}>
    {children}
  </TooltipPrimitive.Provider>
)
